import { type CSSProperties } from "react";
import toast from "react-hot-toast";
import { formatCurrency } from "@veorest/utils";
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  Bike,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Globe,
  MapPin,
  MessageCircle,
  Monitor,
  Package,
  PackageCheck,
  PenLine,
  Phone,
  PhoneCall,
  Route,
  RotateCcw,
  Sparkles,
  Store,
  Truck,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  partnerStageTranslationKey,
  type PartnerDriverRow,
  type PartnerOrderDetail,
  type PartnerOrderInfo,
  type PartnerOrderStage,
} from "../../lib/partnerOrders";
import { useI18n, type TranslationKey } from "../../i18n";
import { appIntlLocale } from "../../lib/partnerTime";
import { OrderAssignDriver } from "./OrderAssignDriver";

const FLOW: Array<{ key: PartnerOrderStage; icon: LucideIcon }> = [
  { key: "pending", icon: Clock },
  { key: "processing", icon: Package },
  { key: "ready", icon: PackageCheck },
  { key: "in_delivery", icon: Bike },
  { key: "completed", icon: Check },
];

/** Stage icons for the header status badge. */
const STAGE_ICONS: Record<PartnerOrderStage, LucideIcon> = {
  pending: Clock,
  processing: Package,
  ready: PackageCheck,
  in_delivery: Bike,
  completed: Check,
  cancelled: X,
};

function formatMoney(amount: number, currency: string, locale: string) {
  return formatCurrency(amount, currency || "IQD", locale);
}

interface OrderDetailPanelProps {
  order: PartnerOrderDetail;
  busy?: boolean;
  drivers?: PartnerDriverRow[];
  driversLoading?: boolean;
  fallbackEnabled?: boolean;
  showFallback?: boolean;
  onClose: () => void;
  onPrimary: () => void;
  onChangeDriver?: () => void;
  onDecline?: () => Promise<void> | void;
  onUnassignDriver: () => Promise<void>;
  onRequestOeloxDriver?: () => Promise<void>;
}

function formatDateTime(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(appIntlLocale(locale), {
      dateStyle: "medium",
      timeStyle: "short",
      numberingSystem: "latn",
    }).format(new Date(iso));
  } catch {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return null;
    }
  }
}

function formatNumber(
  value: number,
  locale: string,
  maxFractionDigits = 1
): string {
  try {
    return new Intl.NumberFormat(appIntlLocale(locale), {
      maximumFractionDigits: maxFractionDigits,
      numberingSystem: "latn",
    }).format(value);
  } catch {
    return String(value);
  }
}

function formatTimeOnly(iso: string, locale: string): string | null {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  try {
    return new Intl.DateTimeFormat(appIntlLocale(locale), {
      hour: "2-digit",
      minute: "2-digit",
      numberingSystem: "latn",
    }).format(new Date(ms));
  } catch {
    return null;
  }
}

const PAYMENT_TYPE_KEY: Record<PartnerOrderInfo["paymentType"], TranslationKey> =
  {
    prepaid: "orders.detail.paymentType.prepaid",
    cod: "orders.detail.paymentType.cod",
    pay_at_cashier: "orders.detail.paymentType.payAtCashier",
    unknown: "orders.detail.paymentType.unknown",
  };

function paymentMethodLabel(
  method: string | null,
  t: (key: TranslationKey) => string
): string | null {
  if (!method) return null;
  const value = method.toLowerCase();
  if (value === "cash") return t("orders.detail.methodCash");
  if (value === "card") return t("orders.detail.methodCard");
  if (value === "wallet") return t("orders.detail.methodWallet");
  if (value === "cod") return t("orders.detail.methodCod");
  if (value.startsWith("pay_at")) return null;
  return method;
}

function paymentStatusLabel(
  status: string | null,
  t: (key: TranslationKey) => string
): string | null {
  if (!status) return null;
  const value = status.toLowerCase();
  if (value === "paid") return t("orders.detail.statusPaid");
  if (value === "unpaid" || value === "pending" || value === "cod_pending") {
    return t("orders.detail.statusUnpaid");
  }
  if (value === "cod_collected") return t("orders.detail.statusCodCollected");
  if (value === "pay_at_pickup_collected") {
    return t("orders.detail.statusPickupCollected");
  }
  if (value === "failed") return t("orders.detail.statusFailed");
  if (value === "refunded") return t("orders.detail.statusRefunded");
  return status;
}

function feePayerLabel(
  responsibility: string | null,
  t: (key: TranslationKey) => string
): string | null {
  if (!responsibility) return null;
  const value = responsibility.toUpperCase();
  if (value === "CUSTOMER_PAYS") return t("orders.detail.feePayerCustomer");
  if (value === "RESTAURANT_PAYS") return t("orders.detail.feePayerRestaurant");
  if (value === "OELOX_PAYS") return t("orders.detail.feePayerOelox");
  if (value === "PARTNER_PAYS") return t("orders.detail.feePayerPartner");
  if (value === "FREE_DELIVERY") return t("orders.detail.feePayerFree");
  return responsibility;
}

function orderMethodLabel(
  source: string | null,
  channel: string | null,
  t: (key: TranslationKey) => string
): { label: string; icon: LucideIcon } | null {
  const src = (source ?? "").toLowerCase();
  const ch = (channel ?? "").toLowerCase();
  if (!src && !ch) return null;
  if (src === "whatsapp_ai")
    return { label: t("orders.method.whatsappAi"), icon: Sparkles };
  if (ch === "whatsapp" || src === "whatsapp")
    return { label: t("orders.method.whatsapp"), icon: MessageCircle };
  if (ch === "storefront" || src === "storefront")
    return { label: t("orders.method.webStore"), icon: Globe };
  if (ch === "marketplace_app" || src === "marketplace_app")
    return { label: t("orders.method.marketplace"), icon: Globe };
  if (ch === "pos" || ch === "cashier")
    return { label: t("orders.method.pos"), icon: Monitor };
  if (ch === "phone")
    return {
      label:
        src === "staff" ? t("orders.method.manual") : t("orders.method.phone"),
      icon: PenLine,
    };
  if (src === "staff" || ch === "manual")
    return { label: t("orders.method.manual"), icon: PenLine };
  const raw = channel ?? source;
  return raw ? { label: raw, icon: Globe } : null;
}

/* ── stat card ─────────────────────────────────────────────── */

interface StatItem {
  icon: LucideIcon;
  label: string;
  value: string;
  accent: string;
}

function StatCard({ icon: Icon, label, value, accent }: StatItem) {
  const iconStyle = { "--odp-c": accent } as CSSProperties;
  return (
    <div className="odp-stat">
      <span className="odp-stat__icon" style={iconStyle}>
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="odp-stat__text">
        <span className="odp-stat__label">{label}</span>
        <span className="odp-stat__value">{value}</span>
      </span>
    </div>
  );
}

/* ── scoped styles (prefix: odp-) ──────────────────────────── */

const PANEL_CSS = `
/* header */
.odp-header{padding:12px 16px 11px;background:var(--surface);border-bottom:1px solid var(--border-subtle);display:flex;flex-direction:column;align-items:stretch}
.odp-eyebrow{display:inline-flex;align-items:center;gap:4px;font-size:13px;font-weight:700;letter-spacing:.02em;color:var(--text-secondary);font-variant-numeric:tabular-nums;margin:0}
.odp-title-row{display:flex;align-items:center;gap:6px;flex-wrap:nowrap;margin:0;min-width:0}
.odp-order-no{display:inline-flex;align-items:center;gap:2px;flex-shrink:0;font-size:13px;font-weight:800;letter-spacing:.02em;color:var(--text-secondary);font-variant-numeric:tabular-nums}
.odp-order-no .odp-icon-btn{width:22px;height:22px}
.odp-store{flex:0 1 auto;min-width:0;font-size:16px;font-weight:800;color:var(--text-primary);margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.odp-title-row .odp-badge{flex-shrink:0}
.odp-badge{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:999px;font-size:12.5px;font-weight:800;letter-spacing:.03em;text-transform:uppercase;line-height:1.6}
.odp-badge--status{--odp-s:var(--accent);background:color-mix(in srgb,var(--odp-s) 12%,transparent);color:var(--odp-s)}
.odp-badge--pending{--odp-s:var(--warning,#b45309)}
.odp-badge--processing{--odp-s:var(--info,#2b7fff)}
.odp-badge--ready{--odp-s:var(--accent)}
.odp-badge--in_delivery{--odp-s:var(--info,#2b7fff)}
.odp-badge--completed{--odp-s:var(--success,#15803d)}
.odp-badge--cancelled{--odp-s:var(--danger,#dc2626)}
.odp-badge--warn{--odp-s:var(--warning,#b45309)}
.odp-badge--method{background:var(--surface-muted);color:var(--text-secondary)}
.odp-close{display:grid;place-items:center;width:30px;height:30px;border-radius:9px;border:1px solid var(--border-subtle);background:var(--surface);color:var(--text-muted);cursor:pointer;flex-shrink:0;transition:background .15s,color .15s,border-color .15s}
.odp-close:hover{background:var(--danger);border-color:var(--danger);color:#fff}

/* flow */
.odp-flow{display:flex;align-items:flex-start;margin:0;width:100%;padding:12px 10px 10px;border:1px solid var(--border-subtle);border-radius:12px;background:var(--surface)}
.odp-flow__step{flex:1;display:flex;flex-direction:column;align-items:center;gap:7px;position:relative;min-width:0}
.odp-flow__step::before{content:"";position:absolute;top:16px;inset-inline-start:calc(-50% + 18px);inset-inline-end:calc(50% + 18px);height:2px;border-radius:2px;background:var(--border)}
.odp-flow__step:first-child::before{display:none}
.odp-flow__step--done::before,.odp-flow__step--active::before{background:var(--accent)}
.odp-flow__dot{display:grid;place-items:center;width:30px;height:30px;border-radius:50%;background:var(--surface);border:2px solid var(--border-subtle);color:var(--text-muted);position:relative;z-index:1;transition:all .22s}
.odp-flow__step--done .odp-flow__dot{background:var(--accent);border-color:var(--accent);color:#fff}
.odp-flow__step--active .odp-flow__dot{border-color:var(--accent);color:var(--accent);box-shadow:0 0 0 4px color-mix(in srgb,var(--accent) 16%,transparent)}
.odp-flow__lbl{font-size:12.5px;font-weight:600;line-height:1.3;text-align:center;color:var(--text-secondary);padding:0 3px;max-width:82px;overflow:hidden;text-overflow:ellipsis}
.odp-flow__step--done .odp-flow__lbl,.odp-flow__step--active .odp-flow__lbl{color:var(--text-primary)}

/* body */
.odp-body{padding:14px 16px 16px;display:flex;flex-direction:column;gap:12px}
.odp-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;align-items:start}
@media (max-width:1159px){.odp-grid{grid-template-columns:1fr}}
.odp-col{display:flex;flex-direction:column;gap:12px;min-width:0}

/* stats – responsive row, wraps instead of truncating labels */
.odp-stats{display:flex;flex-wrap:wrap;gap:6px}
.odp-stat{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:11px;background:var(--surface);border:1px solid var(--border-subtle);flex:1 1 150px;min-width:150px;transition:border-color .15s,background .15s}
.odp-stat:hover{border-color:color-mix(in srgb,var(--accent) 26%,var(--border-subtle))}
.odp-stat__icon{--odp-c:var(--accent);display:grid;place-items:center;width:30px;height:30px;border-radius:9px;flex-shrink:0;color:var(--odp-c);background:color-mix(in srgb,var(--odp-c) 12%,transparent)}
.odp-stat__text{display:flex;flex-direction:column;min-width:0;flex:1}
.odp-stat__label{font-size:12.5px;font-weight:600;line-height:1.25;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.odp-stat__value{font-size:14.5px;font-weight:700;line-height:1.3;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

/* cards */
.odp-card{background:var(--surface);border:1px solid var(--border-subtle);border-radius:12px;padding:12px 14px;min-width:0;transition:border-color .15s}
.odp-card:hover{border-color:color-mix(in srgb,var(--accent) 22%,var(--border-subtle))}
.odp-card__title{display:flex;align-items:center;gap:6px;margin:0 0 8px;font-size:12.5px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:var(--text-secondary)}
.odp-card__title svg{opacity:.75}
.odp-name{margin:0 0 8px;font-size:14.5px;font-weight:800;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis}

/* contact rows */
.odp-contact{display:flex;align-items:center;gap:8px;padding:6px 9px;border-radius:9px;background:var(--surface-muted);border:1px solid var(--border-subtle);min-width:0;transition:background .15s,border-color .15s}
.odp-contact + .odp-contact{margin-top:7px}
.odp-contact:hover{background:var(--surface);border-color:color-mix(in srgb,var(--accent) 22%,var(--border-subtle))}
.odp-contact__icon{color:var(--text-muted);flex-shrink:0}
.odp-contact__body{flex:1;min-width:0;font-size:14.5px;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.odp-contact__actions{display:flex;gap:4px;flex-shrink:0}
.odp-icon-btn{display:inline-grid;place-items:center;width:28px;height:28px;border-radius:8px;border:none;background:transparent;color:var(--text-muted);cursor:pointer;transition:background .15s,color .15s}
.odp-icon-btn:hover{background:var(--accent);color:#fff}

/* meta */
.odp-meta{display:flex;align-items:center;gap:6px;padding:3px 0;font-size:14px;color:var(--text-secondary)}
.odp-meta strong{margin-inline-start:auto;font-weight:600;color:var(--text-primary);text-align:end}

/* key / value */
.odp-kv{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:5.5px 0;font-size:14.5px}
.odp-kv + .odp-kv{border-top:1px solid var(--border-subtle)}
.odp-kv__k{color:var(--text-secondary)}
.odp-kv__v{font-weight:700;color:var(--text-primary);text-align:end;font-variant-numeric:tabular-nums}

/* items */
.odp-line{display:flex;align-items:baseline;gap:8px;padding:6px 0;font-size:14.5px;color:var(--text-secondary)}
.odp-item + .odp-item .odp-line{border-top:1px dashed var(--border-subtle)}
.odp-line__qty{font-weight:700;color:var(--accent);flex-shrink:0;min-width:24px;font-variant-numeric:tabular-nums}
.odp-line__name{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.odp-line__price{font-weight:600;color:var(--text-primary);white-space:nowrap;flex-shrink:0;font-variant-numeric:tabular-nums}
.odp-line__note{margin:-2px 0 6px;padding-inline-start:30px;font-size:13px;color:var(--text-secondary)}
.odp-total{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:7px 0 2px;font-size:16.5px;font-weight:800;color:var(--text-primary);font-variant-numeric:tabular-nums}
.odp-rule{border:0;border-top:1px dashed var(--border-subtle);margin:7px 0}
.odp-rule--double{border-top-style:solid;border-top-width:2px;opacity:.5}
.odp-muted{margin:0;padding:6px 0;font-size:14.5px;color:var(--text-secondary)}

/* notes */
.odp-notes{padding:9px 11px;border-radius:10px;font-size:14.5px;line-height:1.5;color:var(--text-secondary);background:color-mix(in srgb,var(--warning) 8%,transparent);border:1px solid color-mix(in srgb,var(--warning) 25%,transparent)}

/* footer */
.odp-footer{padding:10px 16px 12px;border-top:1px solid var(--border-subtle);background:var(--surface)}
.odp-footer__row{display:flex;align-items:center;gap:8px}
.odp-footer .btn-primary{border-radius:11px;font-weight:700;min-height:44px}
.odp-footer .btn-primary:active:not(:disabled){transform:scale(.985)}
.odp-decline{min-height:44px;padding:0 14px;border-radius:11px;border:1.5px solid color-mix(in srgb,var(--danger) 32%,transparent);background:var(--surface);color:var(--danger);font-size:13px;font-weight:700;cursor:pointer;transition:background .15s,border-color .15s}
.odp-decline:hover:not(:disabled){background:var(--danger-light);border-color:color-mix(in srgb,var(--danger) 55%,transparent)}
.odp-decline:disabled{opacity:.55;cursor:not-allowed}

/* more menu */
.odp-more{position:relative}
.odp-more__trigger{display:inline-flex;align-items:center;gap:5px;min-height:44px;padding:8px 14px;border-radius:11px;border:1px solid var(--border-subtle);background:var(--surface);color:var(--text-secondary);font-size:13px;font-weight:600;cursor:pointer;transition:border-color .15s,color .15s,background .15s}
.odp-more__trigger:hover:not(:disabled){border-color:color-mix(in srgb,var(--accent) 32%,var(--border-subtle));color:var(--accent)}
.odp-more__trigger:disabled{opacity:.5;cursor:not-allowed}
.odp-more__menu{position:absolute;bottom:calc(100% + 6px);inset-inline-end:0;min-width:186px;border-radius:12px;overflow:hidden;z-index:40;background:var(--surface);border:1px solid var(--border);box-shadow:var(--shadow-popover);animation:odpPop .15s ease}
@keyframes odpPop{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
.odp-more__item{display:flex;align-items:center;gap:8px;width:100%;padding:9px 13px;border:0;background:none;font-size:13px;font-weight:600;color:var(--text-secondary);cursor:pointer;text-align:start;transition:background .12s}
.odp-more__item:hover{background:var(--surface-hover)}
.odp-more__item--danger{color:var(--danger,#dc2626)}
.odp-more__item--danger:hover{background:var(--danger-light)}

/* empty */
.odp-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:10px;padding:36px;text-align:center}
.odp-empty__icon{display:grid;place-items:center;width:48px;height:48px;border-radius:14px;background:var(--surface-muted);color:var(--text-muted)}
.odp-empty__title{margin:0;font-size:16px;font-weight:700;color:var(--text-secondary)}
.odp-empty__hint{margin:0;font-size:14px;color:var(--text-muted)}

/* clarity: mobile back */
.odp-back{display:none}
@media (max-width:767px){.odp-back{display:inline-flex;align-items:center;gap:6px;align-self:flex-start;margin:0 0 10px;padding:7px 12px;border-radius:10px;border:1px solid var(--border-subtle);background:var(--surface-muted);color:var(--text-secondary);font-size:12.5px;font-weight:700;cursor:pointer}}
`;

export function OrderDetailPanel({
  order,
  busy,
  drivers = [],
  driversLoading,
  fallbackEnabled,
  showFallback,
  onClose,
  onPrimary,
  onChangeDriver,
  onDecline,
  onUnassignDriver,
  onRequestOeloxDriver,
}: OrderDetailPanelProps) {
  const { t, locale, direction } = useI18n();

  const copyValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("common.copied"));
    } catch {
      toast.error(t("common.copyFailed"));
    }
  };

  const stepIndex = Math.max(
    0,
    FLOW.findIndex((s) => s.key === order.stage)
  );
  const StageIcon = STAGE_ICONS[order.stage];
  const isTerminal = order.stage === "completed" || order.stage === "cancelled";
  const showAccept =
    order.stage === "pending" && order.fulfillment_type === "delivery";
  const showAssign =
    (order.stage === "ready" || order.stage === "in_delivery") &&
    order.fulfillment_type === "delivery";

  const createdAt = formatDateTime(order.created_at, locale);
  const acceptedAt = formatDateTime(order.partner_accepted_at, locale);
  const mapsHref = order.customer_address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        order.customer_address
      )}`
    : null;

  const info = order.info;
  const isPickupOrder = order.fulfillment_type === "pickup";
  const itemCount = order.items.reduce(
    (sum, item) => sum + (Number.isFinite(item.quantity) ? item.quantity : 0),
    0
  );
  const kmLabel =
    !isPickupOrder && info.deliveryKm != null
      ? t("orders.detail.km", { value: formatNumber(info.deliveryKm, locale) })
      : null;
  const readyTime = info.readyAt ? formatTimeOnly(info.readyAt, locale) : null;
  const pickupLabel = info.readyAt
    ? formatDateTime(info.readyAt, locale)
    : null;
  const method = orderMethodLabel(order.source, order.channel, t);
  const MethodIcon = method?.icon ?? null;
  const prepLabel =
    info.prepMinutes != null && info.prepMinutes > 0
      ? t("orders.detail.prepMinutes", { minutes: info.prepMinutes })
      : readyTime;
  const typeLabel =
    info.paymentType !== "unknown"
      ? t(PAYMENT_TYPE_KEY[info.paymentType])
      : null;
  const methodLabel = paymentMethodLabel(info.paymentMethod, t);
  const statusLabel = paymentStatusLabel(info.paymentStatus, t);
  const payerLabel = feePayerLabel(info.feeResponsibility, t);
  const feeText =
    info.feeResponsibility === "FREE_DELIVERY"
      ? payerLabel
      : info.deliveryFee != null && info.deliveryFee > 0
        ? [formatMoney(info.deliveryFee, order.currency, locale), payerLabel]
            .filter(Boolean)
            .join(" · ")
        : payerLabel;
  const paymentParts = [typeLabel, methodLabel].filter(
    (value, index, all): value is string =>
      !!value && all.indexOf(value) === index
  );

  const paymentRows: Array<{ label: string; value: string }> = [];
  if (paymentParts.length > 0) {
    paymentRows.push({
      label: t("orders.detail.method"),
      value: paymentParts.join(" · "),
    });
  }
  if (statusLabel) {
    paymentRows.push({
      label: t("orders.detail.statusLabel"),
      value: statusLabel,
    });
  }
  if (info.cashCollector === "driver") {
    paymentRows.push({
      label: t("orders.detail.cashCollector"),
      value: t("orders.detail.driverCollects"),
    });
  }
  if (feeText) {
    paymentRows.push({ label: t("orders.detail.deliveryFee"), value: feeText });
  }
  if (info.partnerFeeAmount != null && info.partnerFeeAmount > 0) {
    const typeSuffix =
      info.partnerFeeType === "percentage"
        ? t("orders.detail.partnerFeePercentage")
        : info.partnerFeeType === "per_order"
          ? t("orders.detail.partnerFeePerOrder")
          : null;
    paymentRows.push({
      label: t("orders.detail.clientCharge"),
      value: [formatMoney(info.partnerFeeAmount, order.currency, locale), typeSuffix]
        .filter(Boolean)
        .join(" · "),
    });
  }

  /* stat cards */
  const stats: StatItem[] = [
    {
      icon: isPickupOrder ? Store : Truck,
      label: t("orders.detail.method"),
      value: t(isPickupOrder ? "common.pickup" : "common.delivery"),
      accent: isPickupOrder ? "var(--accent)" : "var(--info, #2b7fff)",
    },
  ];
  if (kmLabel) {
    stats.push({
      icon: Route,
      label: t("common.delivery"),
      value: kmLabel,
      accent: "var(--info, #2b7fff)",
    });
  }
  if (typeLabel) {
    stats.push({
      icon: Banknote,
      label: t("orders.detail.payment"),
      value: typeLabel,
      accent:
        info.paymentType === "cod" ? "var(--warning, #b45309)" : "var(--success, #15803d)",
    });
  }
  if (prepLabel) {
    stats.push({
      icon: Clock,
      label: t("orders.detail.pickupReady"),
      value: prepLabel,
      accent: "var(--accent)",
    });
  }
  if (itemCount > 0) {
    stats.push({
      icon: Package,
      label: t("orders.items"),
      value: t("orders.detail.itemsCount", { count: itemCount }),
      accent: "var(--accent)",
    });
  }

  return (
    <div className="order-detail customer-detail-fade">
      <style>{PANEL_CSS}</style>

      <header className="order-detail__header odp-header">
        <button type="button" className="odp-back" onClick={onClose}>
          {direction === "rtl" ? (
            <ArrowRight className="h-4 w-4" aria-hidden />
          ) : (
            <ArrowLeft className="h-4 w-4" aria-hidden />
          )}
          {t("orders.backToList")}
        </button>
        <div className="flex w-full items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="odp-title-row">
              <span className="odp-order-no">
                {order.displayNumber}
                <button
                  type="button"
                  className="odp-icon-btn"
                  onClick={() => void copyValue(order.displayNumber)}
                  title={t("orders.detail.copyNumber")}
                  aria-label={t("orders.detail.copyNumber")}
                >
                  <Copy className="h-3 w-3" />
                </button>
              </span>
              <h2 className="odp-store">{order.tenant_name}</h2>
              <span
                className={`odp-badge odp-badge--status odp-badge--${order.stage}`}
              >
                <StageIcon className="h-3.5 w-3.5" aria-hidden />
                {t(partnerStageTranslationKey(order.stage))}
              </span>
              {method && MethodIcon ? (
                <span className="odp-badge odp-badge--method">
                  <MethodIcon className="h-3.5 w-3.5" aria-hidden />
                  {method.label}
                </span>
              ) : null}
              {order.info.returnToStore || order.status === "rejected" ? (
                <span className="odp-badge odp-badge--status odp-badge--warn">
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                  {t("orders.detail.returnToStore")}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="odp-close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

      </header>

      <div className="order-detail__body odp-body">
        <div className="odp-flow" aria-hidden>
          {FLOW.map((step, index) => {
            const Icon = step.icon;
            const done = index < stepIndex;
            const active = index === stepIndex;
            return (
              <div
                key={step.key}
                className={`odp-flow__step ${done ? "odp-flow__step--done" : ""} ${
                  active ? "odp-flow__step--active" : ""
                }`}
              >
                <span className="odp-flow__dot">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="odp-flow__lbl">
                  {t(partnerStageTranslationKey(step.key))}
                </span>
              </div>
            );
          })}
        </div>
        <div className="odp-stats">
          {stats.map((item, index) => (
            <StatCard
              key={`${item.label}-${index}`}
              icon={item.icon}
              label={item.label}
              value={item.value}
              accent={item.accent}
            />
          ))}
        </div>

        <div className="odp-grid">
          <div className="odp-col">
            {/* Customer */}
            <section className="odp-card">
              <p className="odp-card__title">
                <Phone className="h-3.5 w-3.5" aria-hidden />
                {t("orders.detail.customer")}
              </p>
              <p className="odp-name">{order.customer_name}</p>

              {order.customer_phone ? (
                <div className="odp-contact">
                  <Phone className="odp-contact__icon h-3.5 w-3.5" />
                  <span className="odp-contact__body" dir="ltr">
                    {order.customer_phone}
                  </span>
                  <span className="odp-contact__actions">
                    <a
                      className="odp-icon-btn"
                      href={`tel:${order.customer_phone}`}
                      title={t("orders.detail.call")}
                      aria-label={t("orders.detail.call")}
                    >
                      <PhoneCall className="h-3.5 w-3.5" />
                    </a>
                    <button
                      type="button"
                      className="odp-icon-btn"
                      onClick={() => void copyValue(order.customer_phone ?? "")}
                      title={t("orders.detail.copyPhone")}
                      aria-label={t("orders.detail.copyPhone")}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              ) : null}

              {order.customer_address ? (
                <div className="odp-contact">
                  <MapPin className="odp-contact__icon h-3.5 w-3.5" />
                  <span className="odp-contact__body">
                    {order.customer_address}
                  </span>
                  <span className="odp-contact__actions">
                    {mapsHref ? (
                      <a
                        className="odp-icon-btn"
                        href={mapsHref}
                        target="_blank"
                        rel="noreferrer"
                        title={t("orders.detail.openMap")}
                        aria-label={t("orders.detail.openMap")}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className="odp-icon-btn"
                      onClick={() =>
                        void copyValue(order.customer_address ?? "")
                      }
                      title={t("orders.detail.copyAddress")}
                      aria-label={t("orders.detail.copyAddress")}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
              ) : null}

              <div style={{ marginTop: 10 }}>
                <div className="odp-meta">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  <span>{t("orders.detail.created")}</span>
                  <strong>{createdAt ?? t("common.dash")}</strong>
                </div>
                {acceptedAt ? (
                  <div className="odp-meta">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                    <span>{t("orders.detail.accepted")}</span>
                    <strong>{acceptedAt}</strong>
                  </div>
                ) : null}
              </div>
            </section>

            {/* Items / receipt */}
            <section className="odp-card">
              <p className="odp-card__title">
                <Package className="h-3.5 w-3.5" aria-hidden />
                {t("orders.items")}
              </p>

              <div className="odp-kv">
                <span className="odp-kv__k">{t("orders.detail.placed")}</span>
                <span className="odp-kv__v">
                  {createdAt ?? t("common.dash")}
                </span>
              </div>
              <div className="odp-kv">
                <span className="odp-kv__k">
                  {t("orders.detail.pickupReady")}
                </span>
                <span className="odp-kv__v">
                  {pickupLabel ?? t("common.dash")}
                </span>
              </div>

              <hr className="odp-rule" />

              {order.items.length === 0 ? (
                <p className="odp-muted">{t("common.dash")}</p>
              ) : (
                <div>
                  {order.items.map((item) => (
                    <div key={item.id} className="odp-item">
                      <div className="odp-line">
                        <span className="odp-line__qty">{item.quantity}×</span>
                        <span className="odp-line__name">{item.name}</span>
                        <span className="odp-line__price">
                          {formatMoney(item.line_total, order.currency, locale)}
                        </span>
                      </div>
                      {item.notes ? (
                        <p className="odp-line__note">{item.notes}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              <hr className="odp-rule odp-rule--double" />

              <div className="odp-total">
                <span>{t("orders.detail.total")}</span>
                <span>{formatMoney(order.total, order.currency, locale)}</span>
              </div>
            </section>

            {order.notes ? (
              <section className="odp-card">
                <p className="odp-card__title">
                  <PenLine className="h-3.5 w-3.5" aria-hidden />
                  {t("orders.notes")}
                </p>
                <p className="odp-notes">{order.notes}</p>
              </section>
            ) : null}
          </div>

          <div className="odp-col">
            {paymentRows.length > 0 ? (
              <section className="odp-card">
                <p className="odp-card__title">
                  <Banknote className="h-3.5 w-3.5" aria-hidden />
                  {t("orders.detail.payment")}
                </p>
                {paymentRows.map((row) => (
                  <div key={row.label} className="odp-kv">
                    <span className="odp-kv__k">{row.label}</span>
                    <span className="odp-kv__v">{row.value}</span>
                  </div>
                ))}
              </section>
            ) : null}

            {showAssign || order.assigned_driver_id ? (
              <OrderAssignDriver
                order={order}
                drivers={drivers}
                loading={driversLoading}
                busy={busy}
                fallbackEnabled={fallbackEnabled}
                showFallback={showFallback}
                onChangeDriver={onChangeDriver}
                onUnassignDriver={onUnassignDriver}
                onRequestOelox={onRequestOeloxDriver}
              />
            ) : null}
          </div>
        </div>
      </div>

      {!isTerminal && (showAccept || (showAssign && !order.assigned_driver_id)) ? (
        <footer className="order-detail__footer odp-footer">
          <div className="odp-footer__row">
            {showAccept ? (
              <button
                type="button"
                className="btn-primary flex-1"
                disabled={busy}
                onClick={onPrimary}
              >
                {t("orders.accept")}
              </button>
            ) : null}
            {showAssign && !order.assigned_driver_id ? (
              <button
                type="button"
                className="btn-primary flex-1"
                disabled={busy}
                onClick={onPrimary}
              >
                {t("orders.assign")}
              </button>
            ) : null}
            {showAccept && onDecline ? (
              <button
                type="button"
                className="odp-decline"
                disabled={busy}
                onClick={() => void onDecline()}
              >
                {t("orders.decline")}
              </button>
            ) : null}
          </div>
        </footer>
      ) : null}
    </div>
  );
}

export function OrderDetailSkeleton() {
  return (
    <div className="order-detail customer-detail-fade" aria-hidden>
      <style>{PANEL_CSS}</style>
      <header className="order-detail__header odp-header">
        <div className="space-y-2">
          <div className="skeleton h-3 w-24 rounded" />
          <div className="skeleton h-6 w-48 rounded" />
          <div className="skeleton h-4 w-32 rounded" />
        </div>
      </header>
      <div className="order-detail__body odp-body">
        <div className="odp-stats">
          <div className="skeleton h-14 rounded-xl flex-1" />
          <div className="skeleton h-14 rounded-xl flex-1" />
          <div className="skeleton h-14 rounded-xl flex-1" />
          <div className="skeleton h-14 rounded-xl flex-1" />
        </div>
        <div className="space-y-2.5">
          <div className="skeleton h-4 w-4/6 rounded" />
          <div className="skeleton h-4 w-full rounded" />
          <div className="skeleton h-4 w-5/6 rounded" />
          <div className="skeleton h-4 w-3/6 rounded" />
        </div>
      </div>
    </div>
  );
}

export function OrderDetailEmpty() {
  const { t } = useI18n();
  return (
    <div className="order-detail-empty odp-empty">
      <style>{PANEL_CSS}</style>
      <span className="odp-empty__icon">
        <Package className="h-6 w-6" />
      </span>
      <p className="odp-empty__title">{t("orders.detailEmpty")}</p>
      <p className="odp-empty__hint">{t("orders.detailEmptyHint")}</p>
    </div>
  );
}